/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { useAsyncEffect } from '../useAsyncEffect/useAsyncEffect';
import { useRef } from 'react';

declare global {
	interface Window {
		hj?: {
			settings?: {
				record?: boolean;
			};
		};
	}
}

/**
 * Credits: https://github.com/stitchesjs/stitches/issues/873#issuecomment-986982820.
 */
export function useHotJarStylesRelay(): void {
	const intervalRef = useRef<number | null>( null );
	const styleElementRef = useRef<HTMLStyleElement | null>( null );

	useAsyncEffect( mountCallback, unmountCallback );

	async function mountCallback() {
		/* v8 ignore start */
		if ( typeof window === 'undefined' ) {
			return;
		}

		/* v8 ignore end */

		await waitForHotJar();

		const syncStylesElement = styleElementRef.current = document.createElement( 'style' );
		syncStylesElement.dataset.hotJarWorkaround = 'true';

		document.head.insertBefore( syncStylesElement, document.head.children[ 0 ] );

		let lastStyles: string;

		/* v8 ignore start */
		intervalRef.current = window.setInterval( () => {
			window.requestAnimationFrame( () => {
				const styles = handleRequestAnimationFrame();

				if ( styles === lastStyles ) {
					return;
				}

				syncStylesElement.textContent = styles;
				lastStyles = styles;
			} );
		}, 500 );

		/* v8 ignore end */
	}

	async function unmountCallback() {
		if ( intervalRef.current ) {
			clearInterval( intervalRef.current );
			intervalRef.current = null;
		}

		if ( styleElementRef.current ) {
			styleElementRef.current.remove();
			styleElementRef.current = null;
		}
	}
}

function waitForHotJar() {
	return new Promise<void>( resolve => {
		const intervalId = window.setInterval( () => {
			const isHotJarRecording = window?.hj?.settings?.record;

			if ( isHotJarRecording ) {
				clearInterval( intervalId );

				resolve();
			}
		}, 50 );
	} );
}

/* v8 ignore start */
function handleRequestAnimationFrame(): string {
	const elements = document.querySelectorAll<HTMLStyleElement>( 'head style' );

	if ( !elements.length ) {
		return '';
	}

	return Array.from( elements )
		.reduce<Array<string>>( ( previousValue, element ) => {
			if ( element.dataset.hotJarWorkaround ) {
				return previousValue;
			}

			if ( !element.sheet ) {
				return previousValue;
			}

			try {
				const rulesString = Array.from( element.sheet.cssRules )
					.reduce<Array<string>>( ( previousRuleValue, rule ) => {
						return previousRuleValue.concat( rule.cssText );
					}, [] );

				return previousValue.concat( rulesString.join( ' ' ) );
			} catch ( error: unknown ) {
				if ( isCssCorsError( error as Error ) ) {
					return previousValue;
				}

				throw error;
			}
		}, [] )
		.join( ' ' );
}

// See: https://github.com/ckeditor/ckeditor5-builder/issues/913.
function isCssCorsError( error: Error ): boolean {
	// Chrome.
	if ( error.message.includes( 'Failed to read the \'cssRules\' property from \'CSSStyleSheet\'' ) ) {
		return true;
	}

	// Firefox.
	if ( error.message.includes( 'CSSStyleSheet.cssRules getter' ) ) {
		return true;
	}

	// Safari.
	if ( error.message.includes( 'Not allowed to access cross-origin stylesheet' ) ) {
		return true;
	}

	return false;
}

/* v8 ignore end */
